import {
  makeApiProvider,
  useApiQuery,
  usePhoenixApiInfiniteQuery,
} from '@pflegenavi/shared-frontend/platform';
import { createContext, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  InfiniteData,
  QueryClient,
  UseMutationResult,
  UseInfiniteQueryResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import type {
  CardConfig,
  GetPliantCardsParams,
  GetPliantCardsResponse,
  GetPliantTransactionsParams,
  GetPliantTransactionsResponse,
  IPliantPhoenixApi,
  IssuePliantCardBody,
  PliantCard,
  PliantTransaction,
  UpdatedPliantCard,
  UpdatePliantCardLimitsBody,
} from './api';
import {
  PliantCardType,
  PliantPhoenixApi,
  PliantStatus,
  PliantType,
} from './api';
import {
  CARD_CONFIG_KEY,
  PLIANT_CARDS_KEY,
  PLIANT_TRANSACTION_KEY,
  PLIANT_TRANSACTIONS_KEY,
} from './queryKeys';
import { EMPLOYEE_CARDS_KEY } from '../user-profile-phoenix/queryKeys';

export type {
  PliantTransaction,
  GetPliantTransactionsParams,
  GetPliantTransactionsResponse,
  PliantCard,
  GetPliantCardsParams,
  GetPliantCardsResponse,
  CardConfig,
  UpdatedPliantCard,
  UpdatePliantCardLimitsBody,
  IssuePliantCardBody,
};
export type {
  DefaultCardConfig,
  MaximumCardConfig,
  PliantCardholderStatus,
} from './api';

const ApiContext = createContext<IPliantPhoenixApi | undefined>(undefined);

const { useApi: usePliantPhoenixApi, ApiProvider: PliantPhoenixApiProvider } =
  makeApiProvider({
    name: 'PliantPhoenixApi',
    ApiContext,
    newApi: (tenantId, auth, apiUrl) =>
      new PliantPhoenixApi(tenantId, auth, apiUrl),
  });

export {
  usePliantPhoenixApi,
  PliantPhoenixApiProvider,
  ApiContext as PliantPhoenixApiContext,
  PliantStatus,
  PliantType,
  PliantCardType,
};

export function usePliantTransaction(
  params: { pliant_transaction_id: string; legal_entity_id: string },
  options?: Omit<
    UseQueryOptions<{ data: PliantTransaction }, unknown>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<{ data: PliantTransaction }, unknown> {
  const select = useCallback((data: { data: PliantTransaction }) => {
    return {
      data: {
        ...data.data,
        booked_at: new Date(data.data.booked_at),
        created_at: new Date(data.data.created_at),
        authorized_at: new Date(data.data.authorized_at),
        confirmed_at: new Date(data.data.confirmed_at),
        reversed_at: data.data.reversed_at
          ? new Date(data.data.reversed_at)
          : null,
      },
    };
  }, []);

  return useApiQuery(
    usePliantPhoenixApi,
    PLIANT_TRANSACTION_KEY(params.pliant_transaction_id),
    (api) =>
      api.getPliantTransaction({
        params: params,
      }),
    {
      ...options,
      select,
    }
  );
}

export function usePliantCards(
  params: GetPliantCardsParams['params'],
  options?: Omit<
    UseQueryOptions<GetPliantCardsResponse, unknown>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<GetPliantCardsResponse, unknown> {
  return useApiQuery(
    usePliantPhoenixApi,
    PLIANT_CARDS_KEY(params),
    (api) => api.getPliantCards({ params }),
    options
  );
}

/** Default and maximum card limits of a legal entity. Disabled without an id. */
export function useCardConfig(
  legalEntityId: string | undefined
): UseQueryResult<CardConfig, unknown> {
  return useApiQuery(
    usePliantPhoenixApi,
    CARD_CONFIG_KEY(legalEntityId),
    async (api) => {
      if (!legalEntityId) {
        throw new Error('legalEntityId is empty');
      }
      return (
        await api.getCardConfig({ params: { legal_entity_id: legalEntityId } })
      ).data;
    },
    // A failure falls back to the backend's checks, so don't keep the dialog waiting.
    { enabled: !!legalEntityId, retry: false }
  );
}

// Writes a card returned by a write endpoint into every cached card list, so the
// change shows before the lists are refetched.
async function applyUpdatedCard(
  queryClient: QueryClient,
  updated: UpdatedPliantCard
): Promise<void> {
  queryClient.setQueriesData<GetPliantCardsResponse>(
    { queryKey: PLIANT_CARDS_KEY() },
    (cached) =>
      cached && {
        ...cached,
        data: cached.data.map((card) =>
          card.pliant_card_id === updated.id
            ? {
                ...card,
                card_status: updated.card_status,
                terminated_at: updated.terminated_at,
                total_limit: updated.total_limit,
                transaction_limit: updated.transaction_limit,
                limit_renew_frequency: updated.limit_renew_frequency,
              }
            : card
        ),
      }
  );
  await invalidateCards(queryClient);
}

async function invalidateCards(queryClient: QueryClient): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: PLIANT_CARDS_KEY() }),
    // The caller's own cards, in case they changed one of them.
    queryClient.invalidateQueries({ queryKey: EMPLOYEE_CARDS_KEY() }),
  ]);
}

/**
 * Updates a card's limits. Pliant applies them asynchronously, but the response
 * already carries the new values.
 */
export function useUpdatePliantCardLimits(): UseMutationResult<
  UpdatedPliantCard,
  unknown,
  { pliantCardId: string; body: UpdatePliantCardLimitsBody }
> {
  const api = usePliantPhoenixApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ pliantCardId, body }) =>
      (
        await api.updatePliantCardLimits({
          params: { pliant_card_id: pliantCardId },
          body,
        })
      ).data,
    onSuccess: (updated) => applyUpdatedCard(queryClient, updated),
  });
}

/**
 * Terminates a card. Pliant terminates asynchronously, but the response already
 * marks the card terminated.
 */
export function useTerminatePliantCard(): UseMutationResult<
  UpdatedPliantCard,
  unknown,
  { pliantCardId: string }
> {
  const api = usePliantPhoenixApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ pliantCardId }) =>
      (
        await api.terminatePliantCard({
          params: { pliant_card_id: pliantCardId },
          body: undefined,
        })
      ).data,
    onSuccess: (updated) => applyUpdatedCard(queryClient, updated),
  });
}

/**
 * Issues a card for an employee, inviting the cardholder first if needed. The
 * card is stored right away in a pending status, so the lists are refetched.
 */
export function useIssuePliantCard(): UseMutationResult<
  UpdatedPliantCard,
  unknown,
  IssuePliantCardBody
> {
  const api = usePliantPhoenixApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body) =>
      (await api.issuePliantCard({ params: undefined, body })).data,
    // Also after an error, e.g. when the list missed an already issued card.
    onSettled: () => invalidateCards(queryClient),
  });
}

/**
 * Number of live cards held by the active employees of a nursing home, issued
 * in its legal entity, split by card type.
 *
 * One list request per card type, each asking for a single row and reading the
 * count off `meta.total_count`. Passing no id disables both queries, so an
 * employee without `nursing_home:manage` never fires them.
 */
export function usePliantCardCount(
  legalEntityId?: string,
  nursingHomeId?: string
): {
  total: number;
  physical: number;
  virtual: number;
} {
  const filters = {
    legal_entity_id: legalEntityId,
    nursing_home_id: nursingHomeId,
    final: false,
    page_size: 1,
  };
  const options = { enabled: !!legalEntityId && !!nursingHomeId };

  const physicalResult = usePliantCards(
    { ...filters, card_type: PliantCardType.PHYSICAL },
    options
  );
  const virtualResult = usePliantCards(
    { ...filters, card_type: PliantCardType.VIRTUAL },
    options
  );

  const physical = physicalResult.data?.meta.total_count ?? 0;
  const virtual = virtualResult.data?.meta.total_count ?? 0;

  return { total: physical + virtual, physical, virtual };
}

export function usePliantTransactions(
  params: GetPliantTransactionsParams,
  options?: Omit<
    UseQueryOptions<GetPliantTransactionsResponse, unknown>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<GetPliantTransactionsResponse, unknown> {
  return useApiQuery(
    usePliantPhoenixApi,
    PLIANT_TRANSACTIONS_KEY(params.params),
    (api) => api.getPliantTransactions(params),
    {
      enabled: params.params.legal_entity_id !== '',
      ...options,
    }
  );
}

/**
 * Params for the open card payment list. The endpoint is legal-entity scoped;
 * `nursingHomeId` narrows it to a single home and is left out at legal entity
 * scope, where the list spans every home in the entity. Pass the scope from
 * `useScopeLegalEntityId()` / `useScopeFilter()`.
 *
 * @example
 * getIncompletePliantTransactionsParams(1, 10, { legalEntityId, nursingHomeId }, undefined)
 */
export function getIncompletePliantTransactionsParams(
  page: number,
  pageSize: number,
  scope: {
    legalEntityId: string | undefined;
    nursingHomeId?: string;
  },
  employeeIds: string[] | undefined,
  filters?: Pick<
    GetPliantTransactionsParams['params'],
    'created_at_from' | 'created_at_to' | 'booked_at_from' | 'booked_at_to'
  >
): GetPliantTransactionsParams {
  return {
    params: {
      page: page,
      page_size: pageSize,
      // An empty id keeps the query disabled until the scope has hydrated.
      legal_entity_id: scope.legalEntityId ?? '',
      // Undefined omits the param entirely, so the backend skips the join.
      nursing_home_id: scope.nursingHomeId,
      employee_ids: employeeIds,
      ...filters,
      mismatch: true,
      pliant_type: [PliantType.PURCHASE, PliantType.CASH_WITHDRAWAL],
      pliant_status: [
        PliantStatus.PENDING,
        PliantStatus.CONFIRMED,
        PliantStatus.BOOKED,
      ],
    },
  };
}

const ALL_PLIANT_TRANSACTIONS_PAGE_SIZE = 500;

// Every matching transaction, fetched page by page, for sums over the whole list.
export function useAllPliantTransactionsPhoenix(
  params: Omit<GetPliantTransactionsParams['params'], 'page' | 'page_size'>
): UseQueryResult<PliantTransaction[], unknown> {
  return useApiQuery(
    usePliantPhoenixApi,
    [...PLIANT_TRANSACTIONS_KEY(params), 'all'],
    async (api) => {
      const transactions: PliantTransaction[] = [];
      for (let page = 1; ; page++) {
        const response = await api.getPliantTransactions({
          params: {
            ...params,
            page,
            page_size: ALL_PLIANT_TRANSACTIONS_PAGE_SIZE,
          },
        });
        transactions.push(...response.data);
        if (!response.meta.has_next_page) {
          return transactions;
        }
      }
    },
    { enabled: params.legal_entity_id !== '' }
  );
}

export function usePliantTransactionsInfiniteQuery({
  legal_entity_id,
  ...otherParams
}: Omit<GetPliantTransactionsParams['params'], 'page' | 'page_size'> & {
  page_size?: number;
}): UseInfiniteQueryResult<
  InfiniteData<GetPliantTransactionsResponse>,
  unknown
> {
  const pageSize = otherParams.page_size ?? 20;

  return usePhoenixApiInfiniteQuery(
    usePliantPhoenixApi,
    PLIANT_TRANSACTIONS_KEY({
      legal_entity_id,
      page: 1,
      page_size: pageSize,
      ...otherParams,
    }),
    async (api, pageParam) => {
      if (legal_entity_id === '') {
        throw new Error('legal_entity_id is empty');
      }

      return await api.getPliantTransactions({
        params: {
          legal_entity_id,
          page: pageParam as number,
          page_size: pageSize,
          ...otherParams,
        },
      });
    },
    { enabled: legal_entity_id !== '' }
  );
}
